import { t } from './textos.js';

/**
 * Regras do Laboratório de Pixels que o tablet decide sozinho (Figma,
 * quadros 18–23). Como no Scan Pixel, a pontuação que vale é a do servidor,
 * que recorrige tudo contra o gabarito dele.
 */

export const PONTOS_POR_PERGUNTA = 10;      // RN-LAB-03
export const PONTOS_DO_BONUS = 20;          // RN-LAB-B06

/** Converte o catálogo do backend (`GET /labpixels/catalogo`) no formato das cenas. */
export function paraAtividades (catalogo) {
    return [...(catalogo?.atividades ?? [])]
        .sort((a, b) => a.ordem - b.ordem)
        .map((atividade) => ({
            id: atividade.id,
            titulo: atividade.titulo,
            tempoSegundos: atividade.tempo_segundos ?? null,
            abertura: atividade.modal_inicio ?? '',
            perguntas: [...atividade.perguntas].sort((a, b) => a.ordem - b.ordem).map((pergunta) => ({
                id: pergunta.id,
                enunciado: pergunta.enunciado,
                bonus: Boolean(pergunta.eh_bonus),
                opcoes: pergunta.opcoes.map((opcao) => ({
                    id: opcao.id, letra: opcao.letra, texto: opcao.texto, correta: Boolean(opcao.correta)
                }))
            }))
        }));
}

/**
 * Separa as perguntas comuns (em ordem, com a atividade de cada uma) da bônus. Cada pergunta comum tem o
 * seu próprio tempo: o da atividade, dividido pelas comuns dela (hoje 360 s para 2 perguntas = 3 min cada).
 */
export function roteiro (atividades) {
    const todas = atividades.flatMap((atividade) => {
        const comuns = atividade.perguntas.filter((pergunta) => !pergunta.bonus).length;
        const tempoPorPergunta = atividade.tempoSegundos && comuns ? Math.round(atividade.tempoSegundos / comuns) : null;
        return atividade.perguntas.map((pergunta) => ({ ...pergunta, atividade, tempoSegundos: tempoPorPergunta }));
    });
    return {
        comuns: todas.filter((pergunta) => !pergunta.bonus),
        bonus: todas.find((pergunta) => pergunta.bonus) ?? null
    };
}

/** Gerador determinístico (mulberry32) a partir de um texto — a mesma semente dá sempre a mesma ordem. */
function geradorDe (semente) {
    let h = 1779033703 ^ String(semente).length;
    for (const c of String(semente)) {
        h = Math.imul(h ^ c.charCodeAt(0), 3432918353);
        h = (h << 13) | (h >>> 19);
    }
    let a = h >>> 0;
    return () => {
        a = (a + 0x6D2B79F5) >>> 0;
        let x = Math.imul(a ^ (a >>> 15), 1 | a);
        x = (x + Math.imul(x ^ (x >>> 7), 61 | x)) ^ x;
        return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
    };
}

/** Embaralha (Fisher-Yates) sem mexer na lista original; com `semente`, o resultado é reproduzível. */
export function embaralhar (lista, semente = null) {
    const sorteio = semente === null ? Math.random : geradorDe(semente);
    const saida = [...lista];
    for (let i = saida.length - 1; i > 0; i--) {
        const j = Math.floor(sorteio() * (i + 1));
        [saida[i], saida[j]] = [saida[j], saida[i]];
    }
    return saida;
}

/** As letras acompanham a posição na tela (A, B, C, D), qualquer que seja a ordem sorteada. */
export function comLetrasPorPosicao (opcoes) {
    return opcoes.map((opcao, i) => ({ ...opcao, letra: 'ABCD'[i] ?? opcao.letra }));
}

export function opcaoCorreta (pergunta) {
    return pergunta.opcoes.find((opcao) => opcao.correta) ?? null;
}

/** "Raissa Pereira Lima" → "RAISSA P." — como o sorteado aparece no modal (quadro 26). */
/**
 * Quanto as alternativas descem para o enunciado não encostar nelas: o que passar do espaço
 * entre o topo do enunciado e o dos cartões (com folga), até o máximo que cabe acima do botão.
 *
 * @param {number} alturaEnunciado altura do enunciado desenhado, em px
 * @param {{ topo: number, topoCartoes: number, folga: number, maximo: number }} area
 * @returns {number} deslocamento dos cartões em px (0..maximo)
 */
export function deslocamentoDasOpcoes (alturaEnunciado, { topo, topoCartoes, folga, maximo }) {
    const excesso = topo + alturaEnunciado + folga - topoCartoes;
    return Math.min(maximo, Math.max(0, Math.ceil(excesso)));
}

export function nomeCurto (nome) {
    const partes = String(nome ?? '').trim().split(/\s+/).filter(Boolean);
    if (partes.length === 0) return '';
    const inicial = partes.length > 1 ? ` ${partes.at(-1)[0]}.` : '';
    return `${partes[0]}${inicial}`.toUpperCase();
}

/** "5:05" — o cronômetro do cabeçalho. */
export function formatarTempo (segundos) {
    const total = Math.max(0, Math.ceil(segundos));
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

const somar = (linhas) => linhas.reduce((soma, linha) => soma + linha.pontos, 0);

/**
 * Os dois cards de pontos da tela de resultados (quadro 72): o **individual** (as perguntas comuns do
 * participante) e o do **grupo** (a bônus, que o ranking credita ao grupo). Cada um com o seu total.
 * `RI-LAB-08`: a linha Bônus só existe se o grupo liberou a pergunta bônus; sem ela o card do grupo
 * fica só com o total (0).
 */
export function montarPlacaresLab ({ comuns, respostas, bonus }) {
    const acertou = (perguntaId) => respostas.some((r) => r.pergunta_id === perguntaId && r.correta);
    const linhasIndividuais = comuns.map((pergunta, i) => ({
        rotulo: t('lab.pergunta', { numero: i + 1 }),
        pontos: acertou(pergunta.id) ? PONTOS_POR_PERGUNTA : 0          // RN-LAB-03 / RN-LAB-04
    }));
    const linhasDoGrupo = bonus?.liberado
        ? [{ rotulo: t('geral.bonus'), pontos: bonus.correta ? PONTOS_DO_BONUS : 0 }]    // RN-LAB-B06 / B07
        : [];
    return {
        individual: { titulo: t('lab.card_individual'), linhas: linhasIndividuais, total: somar(linhasIndividuais) },
        grupo: { titulo: t('lab.card_grupo'), linhas: linhasDoGrupo, total: somar(linhasDoGrupo) }
    };
}

/** As mesmas linhas num placar só (perguntas e bônus juntas), para quem precisa do total geral. */
export function montarPlacarLab (entrada) {
    const { individual, grupo } = montarPlacaresLab(entrada);
    const linhas = [...individual.linhas, ...grupo.linhas];
    return { linhas, total: somar(linhas) };
}

const CHAVE = (participanteId) => `fiat-arcade:labpixels:v1:${participanteId}`;

const novoEstado = () => ({
    inicio: null,
    fim: null,               // conclusão das perguntas comuns (RN-LAB-06)
    indice: 0,               // quantas perguntas comuns já passaram (respondidas ou perdidas no tempo)
    respostas: [],           // só as comuns
    prazos: {},              // { [perguntaId]: ISO } — fim do tempo de cada pergunta comum
    ordens: {},              // { [perguntaId]: [opcaoId] } — ordem sorteada das alternativas, fixa ao recarregar
    estourou: false,         // alguma atividade acabou por tempo (RN-LAB-B01)
    chaveEnvio: null,
    enviado: false,
    bonus: { liberado: false, sorteado: null, semente: null, liberadoEm: null, resposta: null, resultadoDoGrupo: null, chaveEnvio: null, enviado: false },
    concluido: false         // chegou à tela de resultados
});

/** Progresso individual na fase (RN-LAB-01 / RN-LAB-06), salvo no tablet. */
export class ProgressoLabPixels {
    constructor (participanteId, { armazenamento = globalThis.localStorage, agora = () => new Date() } = {}) {
        this._chave = CHAVE(participanteId);
        this._armazenamento = armazenamento;
        this._agora = agora;
        this.estado = novoEstado();
        try {
            const bruto = armazenamento?.getItem(this._chave);
            if (bruto) {
                const salvo = JSON.parse(bruto);
                this.estado = { ...novoEstado(), ...salvo, bonus: { ...novoEstado().bonus, ...salvo.bonus } };
            }
        } catch { /* estado novo */ }
    }

    iniciar () {
        if (!this.estado.inicio) {
            this.estado.inicio = this._agora().toISOString();
            this._gravar();
        }
    }

    /**
     * Alternativas na ordem sorteada, sem padrão entre perguntas. A das comuns é sorteada uma vez e guardada
     * (recarregar o app não muda o layout); a do bônus vem da semente do servidor, igual para a turma toda.
     */
    opcoesDa (pergunta) {
        if (pergunta.bonus) {
            const semente = this.estado.bonus.semente ?? pergunta.id;
            return comLetrasPorPosicao(embaralhar(pergunta.opcoes, `${semente}|${pergunta.id}`));
        }
        let ids = this.estado.ordens[pergunta.id];
        if (!ids || ids.length !== pergunta.opcoes.length) {
            ids = embaralhar(pergunta.opcoes).map((opcao) => opcao.id);
            this.estado.ordens[pergunta.id] = ids;
            this._gravar();
        }
        const porId = new Map(pergunta.opcoes.map((opcao) => [opcao.id, opcao]));
        return comLetrasPorPosicao(ids.map((id) => porId.get(id)).filter(Boolean));
    }

    /** Prazo da pergunta: fixado na primeira vez, para recarregar o app não devolver tempo. */
    prazoDe (pergunta) {
        if (!pergunta.tempoSegundos) return null;
        if (!this.estado.prazos[pergunta.id]) {
            this.estado.prazos[pergunta.id] = new Date(this._agora().getTime() + pergunta.tempoSegundos * 1000).toISOString();
            this._gravar();
        }
        return new Date(this.estado.prazos[pergunta.id]);
    }

    /** O tempo da pergunta já começou a correr neste tablet. */
    iniciouPrazo (pergunta) {
        return Boolean(this.estado.prazos[pergunta.id]);
    }

    /** O facilitador pausou o grupo: o tempo parado não é descontado de nenhum prazo em curso. */
    adiarPrazos (milissegundos) {
        if (!(milissegundos > 0)) return;
        for (const [id, prazo] of Object.entries(this.estado.prazos)) {
            this.estado.prazos[id] = new Date(new Date(prazo).getTime() + milissegundos).toISOString();
        }
        this._gravar();
    }

    segundosRestantes (pergunta) {
        const prazo = this.prazoDe(pergunta);
        return prazo ? (prazo.getTime() - this._agora().getTime()) / 1000 : Infinity;
    }

    /**
     * Quantas perguntas comuns foram de fato respondidas. É o que o painel do
     * facilitador acompanha: pergunta perdida no tempo não conta, e a bônus só
     * fica elegível com todos respondendo todas.
     */
    get respondidas () {
        return this.estado.respostas.length;
    }

    /**
     * Resposta conferida é definitiva: grava e já conta a pergunta como
     * passada, para recarregar o app não permitir responder de novo.
     */
    responder (pergunta, opcao) {
        if (this.estado.respostas.some((r) => r.pergunta_id === pergunta.id)) return;
        this.estado.indice += 1;
        this.estado.respostas.push({
            pergunta_id: pergunta.id, opcao_id: opcao.id, correta: opcao.correta,
            timestamp_tablet: this._agora().toISOString()
        });
        this._gravar();
    }

    /** Tempo da pergunta esgotado: ela fica sem resposta (e a bônus se perde). */
    estourar (ateIndice) {
        this.estado.estourou = true;
        this.estado.indice = Math.max(this.estado.indice, ateIndice);
        this._gravar();
    }

    fecharComuns () {
        if (!this.estado.fim) {
            this.estado.fim = this._agora().toISOString();
            this._gravar();
        }
    }

    /** Corpo de `POST /labpixels/resultado` e a chave de idempotência, sempre a mesma. */
    envio (gerarChave = () => globalThis.crypto.randomUUID()) {
        if (!this.estado.chaveEnvio) {
            this.estado.chaveEnvio = gerarChave();
            this._gravar();
        }
        return {
            chave: this.estado.chaveEnvio,
            corpo: { inicio_tablet: this.estado.inicio, fim_tablet: this.estado.fim, respostas: this.estado.respostas }
        };
    }

    marcarEnviado () {
        this.estado.enviado = true;
        this._gravar();
    }

    liberarBonus ({ sorteado, semente = null }) {
        Object.assign(this.estado.bonus, {
            liberado: true, sorteado, semente, liberadoEm: this._agora().toISOString()
        });
        this._gravar();
    }

    responderBonus (pergunta, opcao) {
        this.estado.bonus.resposta = {
            pergunta_id: pergunta.id, opcao_id: opcao.id, correta: opcao.correta,
            timestamp_tablet: this._agora().toISOString()
        };
        this._gravar();
    }

    /**
     * Quem não é o sorteado não responde a bônus, mas vê a conferência dele em tempo real: guarda se o
     * grupo acertou para o card de pontos do grupo (sem isso a tela mostraria 0 de bônus a todos eles).
     */
    registrarBonusDoGrupo (correta) {
        this.estado.bonus.resultadoDoGrupo = { correta: Boolean(correta) };
        this._gravar();
    }

    /** Se o grupo acertou a bônus: a resposta do sorteado, ou a conferência que os demais viram. */
    get bonusCorreta () {
        const { resposta, resultadoDoGrupo } = this.estado.bonus;
        return Boolean(resposta?.correta ?? resultadoDoGrupo?.correta);
    }

    /** Corpo de `POST /labpixels/resultado-grupo`: enviado só pelo tablet do sorteado. */
    envioDoGrupo (gerarChave = () => globalThis.crypto.randomUUID()) {
        const { bonus } = this.estado;
        if (!bonus.chaveEnvio) {
            bonus.chaveEnvio = gerarChave();
            this._gravar();
        }
        return {
            chave: bonus.chaveEnvio,
            corpo: {
                bonus_liberado_em: bonus.liberadoEm,
                participante_sorteado_id: bonus.sorteado?.id ?? null,
                semente: bonus.semente,
                sorteado_em: bonus.liberadoEm,
                resposta: bonus.resposta
            }
        };
    }

    marcarGrupoEnviado () {
        this.estado.bonus.enviado = true;
        this._gravar();
    }

    concluir () {
        this.estado.concluido = true;
        this._gravar();
    }

    reiniciar () {
        this.estado = novoEstado();
        this._gravar();
    }

    _gravar () {
        try { this._armazenamento?.setItem(this._chave, JSON.stringify(this.estado)); } catch { /* sem storage */ }
    }
}
